import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CompleteIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Add as AddIcon,
  BugReport as TestIcon
} from '@mui/icons-material';
import { useTDD } from '../../context/TDDContext';
import TDDService from '../../services/tddService';

/**
 * TDD Task Manager Component
 * Manages atomic TDD tasks following Ralphio's "One Task Per Loop" principle
 */
const TDDTaskManager = ({ projectId }) => {
  const {
    tddState,
    updateCurrentTask,
    updateProgress,
    updateTDDCycle,
    updateWorkspace
  } = useTDD();

  const [tasks, setTasks] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTaskData, setNewTaskData] = useState({ title: '', description: '' });
  const [isExecutingTask, setIsExecutingTask] = useState(false);
  const [taskResults, setTaskResults] = useState({});

  /**
   * Load real tasks from backend based on project conversations
   */
  useEffect(() => {
    const loadTasks = async () => {
      try {
        // First, check if workspace is ready, if not try to initialize
        if (tddState.workspace.status === 'not_initialized') {
          console.log('Workspace not initialized, attempting to initialize...');
          try {
            // Try to initialize workspace
            const initResponse = await TDDService.initializeWorkspace(projectId);
            if (initResponse.success) {
              updateWorkspace({
                status: 'ready',
                path: initResponse.workspace_path
              });
            }
          } catch (initError) {
            console.warn('Could not initialize workspace:', initError);
            // Continue to try loading tasks anyway
          }
        }

        // Get real tasks from the TDD progress endpoint
        const progressResponse = await TDDService.getProgress(projectId);
        console.log('Progress response:', progressResponse);
        
        if (progressResponse.success && progressResponse.data) {
          // Check if we have story_backlog in response
          const storyBacklog = progressResponse.data.story_backlog || progressResponse.story_backlog || [];
          
          if (storyBacklog.length > 0) {
            const realTasks = storyBacklog.map((story, index) => ({
              id: story.id || `story-${index + 1}`,
              title: story.title || `Story ${index + 1}`,
              description: story.description || 'No description available',
              status: story.status || 'pending',
              storyPoints: Math.min(story.effort || story.estimated_effort || 2, 2), // Ensure Ralphio constraint
              category: story.category || 'feature',
              priority: story.priority || 'medium',
              atomicTasks: story.atomic_tasks || story.tasks || [
                { 
                  id: `${story.id || index}-atomic-1`, 
                  title: `Test implementation for ${story.title}`, 
                  status: 'pending' 
                },
                { 
                  id: `${story.id || index}-atomic-2`, 
                  title: `Core logic for ${story.title}`, 
                  status: 'pending' 
                },
                { 
                  id: `${story.id || index}-atomic-3`, 
                  title: `Integration for ${story.title}`, 
                  status: 'pending' 
                }
              ]
            }));
            
            setTasks(realTasks);
            console.log(`Loaded ${realTasks.length} real tasks from project data`);
          } else {
            console.log('No story backlog found, trying to get next task...');
            // Continue with fallback
          }
        } else {
          // Fallback: Try to get next task
          try {
            const nextTaskResponse = await TDDService.getNextTask(projectId);
            if (nextTaskResponse.success && nextTaskResponse.task) {
              const nextTask = nextTaskResponse.task;
              setTasks([{
                id: nextTask.id || 'next-task-1',
                title: nextTask.title || 'Next Development Task',
                description: nextTask.description || 'Continue development workflow',
                status: 'pending',
                storyPoints: 2,
                category: nextTask.category || 'development',
                priority: 'high',
                atomicTasks: [
                  { id: 'atomic-next-1', title: 'Write failing tests', status: 'pending' },
                  { id: 'atomic-next-2', title: 'Implement minimal solution', status: 'pending' },
                  { id: 'atomic-next-3', title: 'Refactor and optimize', status: 'pending' }
                ]
              }]);
            }
          } catch (nextTaskError) {
            console.warn('Could not load next task:', nextTaskError);
            // Show empty state
            setTasks([]);
          }
        }
      } catch (error) {
        console.error('Failed to load real tasks:', error);
        // Show empty state instead of sample data
        setTasks([]);
      }
    };

    loadTasks();
  }, [projectId, tddState.workspace.status]);

  /**
   * Execute a TDD cycle for a specific task
   */
  const executeTDDCycle = async (task) => {
    setIsExecutingTask(true);
    
    try {
      updateCurrentTask({
        id: task.id,
        title: task.title,
        description: task.description,
        status: 'in_progress'
      });

      updateTDDCycle({
        currentCycle: tddState.tddCycle.currentCycle + 1,
        cyclePhase: 'red' // Start with failing tests
      });

      updateProgress({
        phase: 'development',
        message: `Executing TDD cycle for: ${task.title}`,
        percentage: 25
      });

      // Execute TDD cycle via backend
      const response = await TDDService.executeTDDCycle(projectId, task.id, {
        workspace_path: tddState.workspace.path,
        task_context: {
          title: task.title,
          description: task.description,
          atomic_tasks: task.atomicTasks
        }
      });

      if (response.success) {
        // Update task status
        setTasks(prevTasks => 
          prevTasks.map(t => 
            t.id === task.id 
              ? { ...t, status: 'completed' }
              : t
          )
        );

        setTaskResults(prev => ({
          ...prev,
          [task.id]: response.result
        }));

        updateProgress({
          phase: 'testing',
          message: `TDD cycle completed for: ${task.title}`,
          percentage: 75
        });

        updateTDDCycle({
          cyclePhase: 'green', // Tests should be passing now
          cycleHistory: [
            ...tddState.tddCycle.cycleHistory,
            {
              taskId: task.id,
              timestamp: new Date().toISOString(),
              result: 'success'
            }
          ]
        });
      }
    } catch (error) {
      console.error('TDD cycle execution failed:', error);
      setTaskResults(prev => ({
        ...prev,
        [task.id]: { error: error.message }
      }));
      
      updateTDDCycle({
        cyclePhase: 'red',
        cycleHistory: [
          ...tddState.tddCycle.cycleHistory,
          {
            taskId: task.id,
            timestamp: new Date().toISOString(),
            result: 'failed',
            error: error.message
          }
        ]
      });
    } finally {
      setIsExecutingTask(false);
    }
  };

  /**
   * Toggle task expansion
   */
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  /**
   * Create new task
   */
  const handleCreateTask = () => {
    const newTask = {
      id: `task-${Date.now()}`,
      title: newTaskData.title,
      description: newTaskData.description,
      status: 'pending',
      storyPoints: 1,
      atomicTasks: []
    };
    
    setTasks(prev => [...prev, newTask]);
    setNewTaskData({ title: '', description: '' });
    setShowCreateDialog(false);
  };

  /**
   * Get status color
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompleteIcon color="success" />;
      case 'in_progress': return <PlayIcon color="info" />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <TestIcon />;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            TDD Tasks
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Add Task
          </Button>
        </Box>

        {/* Current TDD Cycle Info */}
        {tddState.tddCycle.currentCycle > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Current TDD Cycle: {tddState.tddCycle.currentCycle} | 
              Phase: {tddState.tddCycle.cyclePhase.toUpperCase()}
            </Typography>
          </Alert>
        )}

        {/* Tasks List */}
        <List>
          {tasks.map(task => (
            <React.Fragment key={task.id}>
              <ListItem
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: tddState.currentTask?.id === task.id ? '#f0f8ff' : 'white'
                }}
              >
                <ListItemIcon>
                  {getStatusIcon(task.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">{task.title}</Typography>
                      <Chip 
                        label={`${task.storyPoints} SP`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={task.status} 
                        size="small" 
                        color={getStatusColor(task.status)} 
                      />
                    </Box>
                  }
                  secondary={task.description}
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PlayIcon />}
                      onClick={() => executeTDDCycle(task)}
                      disabled={isExecutingTask || task.status === 'completed'}
                    >
                      {task.status === 'pending' ? 'Start' : 'Resume'}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => toggleTaskExpansion(task.id)}
                    >
                      {expandedTasks[task.id] ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>

              {/* Atomic Tasks Expansion */}
              <Collapse in={expandedTasks[task.id]} timeout="auto" unmountOnExit>
                <Box sx={{ ml: 4, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Atomic Tasks (Ralphio "One Task Per Loop"):
                  </Typography>
                  <List dense>
                    {task.atomicTasks.map(atomicTask => (
                      <ListItem key={atomicTask.id} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {getStatusIcon(atomicTask.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={atomicTask.title}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  {/* Task Results */}
                  {taskResults[task.id] && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Last Result:
                      </Typography>
                      <Typography variant="body2">
                        {taskResults[task.id].error || 'Task completed successfully'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>

        {tasks.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary" gutterBottom>
              {tddState.workspace.status === 'ready' 
                ? 'No tasks loaded from project data yet.' 
                : 'Initialize workspace first to load project tasks.'}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setShowCreateDialog(true)}
              sx={{ mt: 2 }}
            >
              Create Test Task
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New TDD Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            fullWidth
            variant="outlined"
            value={newTaskData.title}
            onChange={(e) => setNewTaskData(prev => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Task Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newTaskData.description}
            onChange={(e) => setNewTaskData(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTask}
            variant="contained"
            disabled={!newTaskData.title.trim()}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TDDTaskManager;